import { TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type { SessionMode } from '@/core/types';

const MODE_LABELS: Record<SessionMode, string> = {
  practice: 'Practice',
  review: 'Review · Recently memorized',
  archive: 'Archive review',
  mistakes: 'Mistakes · Keeps getting you',
  lesson: 'Revision · One lesson',
  topic: 'Revision · One topic',
};

type Props = {
  mode: SessionMode;
  onQuit: () => void;
  current: number; // 1-based
  total: number;
  /**
   * Whether the card on screen is flagged as struggling.
   *
   * The badge lives in the shell rather than in each app's CardFront: it is the
   * same fact about the same engine state in all four apps, so a fifth plugin
   * hook would buy nothing but four copies of one line.
   */
  struggling?: boolean;
  children: ReactNode;
};

export function PracticeShell({
  mode,
  onQuit,
  current,
  total,
  struggling = false,
  children,
}: Props) {
  const pct = total > 0 ? (current / total) * 100 : 0;
  return (
    <div className="bg-background flex min-h-screen flex-col">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={onQuit}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
            Quit
          </Button>
          <div className="flex items-center gap-3">
            {/* Only outside the mistakes mode: in a session made entirely of
                flagged cards the badge would mark every card and say nothing. */}
            {struggling && mode !== 'mistakes' && (
              <span
                className="text-warning inline-flex items-center gap-1 text-xs font-medium"
                title="You have missed this one three times in a row"
              >
                <TriangleAlert className="size-3.5" />
                Keeps getting you
              </span>
            )}
            <div className="text-muted-foreground text-sm font-medium">{MODE_LABELS[mode]}</div>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="mx-auto flex max-w-3xl flex-col px-4 py-6 sm:px-6 sm:py-10">{children}</div>
      </main>

      <footer className="border-border bg-card border-t">
        <div className="mx-auto flex max-w-3xl flex-col gap-2 px-4 py-3 sm:px-6">
          <div className="text-muted-foreground text-xs font-medium">
            Card {current} of {total}
          </div>
          <Progress value={pct} />
        </div>
      </footer>
    </div>
  );
}
